import { useState } from 'react';
import { Table } from '@rich-products-corp/primitives-richproducts';
import { ColumnSizing, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import _ from 'lodash';
import { RecipientTableMobile } from './table-mobile';
import { isInvalidAddressValidationStatus } from '@/helpers/addressValidationStatus';

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
};

export const RecipientsBookTable = <TData, TValue>({ columns, data }: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});

  // const _sortedData = [..._.sortBy(data, (obj: any) => obj.lastName.toLowerCase())];
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
  });

  return (
    <>
      <div className="hidden max-h-[300px] lg:flex">
        <Table className="table-auto">
          <Table.Header className="sticky top-0 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.Head key={headerGroup.id} className="font-semibold">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Head>
                ))}
              </Table.Row>
            ))}
          </Table.Header>

          <Table.Body className="overflow-y-auto">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                // Get validation status from row data (added during parseAddressList)
                const validationStatus = (row.original as any).validationStatus;
                const isInvalid = isInvalidAddressValidationStatus(validationStatus);

                return (
                  <Table.Row
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`px-4 ${isInvalid ? 'border border-red-200 bg-red-50' : ''}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Cell>
                    ))}
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Cell colSpan={columns.length} className="h-24">
                  No results.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};
